import styles from './catalog-picker-modal.module.css'

import { RxArrowLeft } from '@react-icons/all-files/rx/RxArrowLeft'
import { RxTrash } from '@react-icons/all-files/rx/RxTrash'
import React, { useMemo, useState } from 'react'

import {
  Box,
  Button,
  Dialog,
  Flex,
  SearchTextField,
  Text,
  TextField,
} from 'components/ds'

import { CatalogList } from './catalog-list'
import type { CatalogGroup, PickerCatalog } from './mock-catalogs'
import { filterCatalogs } from './utils'

type GroupEditorProps = {
  group: CatalogGroup
  catalogs: PickerCatalog[]
  onBack: () => void
  onSave: (group: CatalogGroup) => void
  onDelete: (groupId: string) => void
}

export const GroupEditor = ({
  group,
  catalogs,
  onBack,
  onSave,
  onDelete,
}: GroupEditorProps) => {
  const [name, setName] = useState(group.name)
  const [catalogIds, setCatalogIds] = useState(group.catalogIds)
  const [query, setQuery] = useState('')

  const visibleCatalogs = useMemo(
    () => filterCatalogs(catalogs, query),
    [catalogs, query],
  )

  const toggleCatalog = (id: string) =>
    setCatalogIds(ids =>
      ids.includes(id) ? ids.filter(i => i !== id) : [...ids, id],
    )

  const canSave = name.trim().length > 0 && catalogIds.length > 0

  return (
    <>
      <Flex align="center" gap="2" mb="4">
        <Button variant="ghost" color="gray" size="1" onClick={onBack}>
          <RxArrowLeft /> Back
        </Button>
      </Flex>
      <Dialog.Title mb="1">Edit group</Dialog.Title>
      <Dialog.Description size="2" color="gray" mb="4">
        Groups let you search the same set of catalogs again in one click.
      </Dialog.Description>

      <Flex direction="column" gap="1" mb="4">
        <Text as="label" size="2" weight="medium" htmlFor="catalog-group-name">
          Group name
        </Text>
        <TextField.Root
          id="catalog-group-name"
          placeholder="e.g. Indie labels"
          value={name}
          onChange={e => setName(e.target.value)}
        />
      </Flex>

      <Flex justify="between" align="center" mb="2">
        <Text size="2" weight="medium">
          Catalogs
        </Text>
        <Text size="1" color="gray">
          {catalogIds.length} selected
        </Text>
      </Flex>
      <SearchTextField
        placeholder="Search catalogs"
        value={query}
        onChange={e => setQuery(e.target.value)}
        onClear={() => setQuery('')}
      />
      <Box className={styles.list} mt="2">
        <CatalogList
          catalogs={visibleCatalogs}
          selectedIds={catalogIds}
          onToggle={toggleCatalog}
        />
      </Box>

      <Flex justify="between" align="center" mt="4">
        <Button variant="ghost" color="red" onClick={() => onDelete(group.id)}>
          <RxTrash /> Delete group
        </Button>
        <Flex gap="3">
          <Button variant="soft" color="gray" onClick={onBack}>
            Cancel
          </Button>
          <Button
            disabled={!canSave}
            onClick={() => onSave({ ...group, name: name.trim(), catalogIds })}
          >
            Save group
          </Button>
        </Flex>
      </Flex>
    </>
  )
}
